"use client"

import { Form, Formik } from "formik"
import { useState } from "react"
import { ChevronDown, Languages, Loader2, Plus } from "lucide-react"
import { FormTextarea } from "@/components/form-inputs"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { useSaveAiDisclosureTemplateMutation } from "@/hooks/mutations/ai-disclosure-settings"
import {
  getDefaultAiDisclosureTemplate,
  renderAiDisclosureTemplate,
  type AiDisclosureTemplateChannel,
} from "@/lib/ai-disclosure/settings"
import { ONBOARDING_LANGUAGE_OPTIONS } from "@/lib/onboarding/languages"
import { AddDisclosureLanguageDialog } from "@/components/organization/add-disclosure-language-dialog"
import styles from "@/components/organization/ai-disclosure-wording-overrides.module.css"

type WordingOverridesProps = {
  channels: readonly AiDisclosureTemplateChannel[]
  templates: Array<{ channel: AiDisclosureTemplateChannel; language: string; template: string }>
  storeName: string
  disabled: boolean
}

const FIELD_LABELS: Record<AiDisclosureTemplateChannel, string> = {
  messenger: "Overwrite Messenger Agent Opening Message",
  email_signature: "Overwrite Email Signature Text",
}

export function AiDisclosureWordingOverrides({ channels, templates, storeName, disabled }: WordingOverridesProps) {
  const [expanded, setExpanded] = useState(false)
  const [addLanguageOpen, setAddLanguageOpen] = useState(false)
  const [addedLanguages, setAddedLanguages] = useState<string[]>([])
  const visibleLanguages = ONBOARDING_LANGUAGE_OPTIONS.filter(language =>
    language.value === "en" || addedLanguages.includes(language.value) ||
    templates.some(template => channels.includes(template.channel) && template.language === language.value),
  )
  const availableLanguages = ONBOARDING_LANGUAGE_OPTIONS.filter(language =>
    !visibleLanguages.some(visible => visible.value === language.value),
  )
  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className={`${styles.editor} min-w-0 rounded-lg border border-primary/20`}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          aria-label="AI announcement wording overwrites"
          className="group flex w-full items-center gap-3 rounded-lg bg-muted/50 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Languages className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">AI announcement wording overwrites</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">Customize the AI announcement for each language.</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </CollapsibleTrigger>
      {/* Keep drafts intact when the merchant collapses the language table. */}
      <CollapsibleContent forceMount hidden={!expanded}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-t px-4 py-3">
          <p className="min-w-0 flex-1 basis-64 text-xs leading-relaxed text-muted-foreground">
            Use <b>[Store]</b> for {storeName}. Leave a field blank to use its default wording.
            Custom wording must clearly identify the assistant as AI. Changes apply to every agent in your organization.
          </p>
          <Button type="button" size="sm" variant="outline" disabled={disabled || availableLanguages.length === 0} onClick={() => setAddLanguageOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add language
          </Button>
        </div>
        <div className="max-h-[32rem] overflow-y-auto rounded-b-lg">
          <table className={`${styles.table} w-full table-fixed`}>
            <caption className="sr-only">AI announcement wording overwrites by language</caption>
            <thead className={`${styles.headers} sticky top-0 z-10 bg-muted`}>
              <tr>
                <th scope="col" className="w-28 px-4 py-2 text-left text-xs font-medium">Language</th>
                {channels.map(channel => (
                  <th key={channel} scope="col" className="px-3 py-2 text-left text-xs font-medium">
                    {FIELD_LABELS[channel]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={styles.body}>
              {visibleLanguages.map(language => (
                <tr key={language.value} className={`${styles.row} min-w-0 border-t`}>
                  <th scope="row" className={`${styles.language} text-left text-sm font-medium`}>
                    {language.label}
                  </th>
                  {channels.map(channel => (
                    <td key={channel} className={`${styles.cell} min-w-0`}>
                      <WordingOverrideField
                        channel={channel}
                        language={language.value}
                        languageLabel={language.label}
                        saved={templates.find(item => item.channel === channel && item.language === language.value)?.template ?? ""}
                        storeName={storeName}
                        disabled={disabled}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
      <AddDisclosureLanguageDialog
        open={addLanguageOpen}
        onOpenChange={setAddLanguageOpen}
        languages={availableLanguages}
        onAdd={language => setAddedLanguages(current => [...current, language])}
      />
    </Collapsible>
  )
}

function WordingOverrideField({ channel, language, languageLabel, saved, storeName, disabled }: {
  channel: AiDisclosureTemplateChannel
  language: string
  languageLabel: string
  saved: string
  storeName: string
  disabled: boolean
}) {
  const save = useSaveAiDisclosureTemplateMutation()
  const defaultTemplate = getDefaultAiDisclosureTemplate(channel, language) ?? ""
  const fieldLabel = FIELD_LABELS[channel]
  const accessibleLabel = `${languageLabel}: ${fieldLabel}`

  return (
    <Formik
      initialValues={{ channel, language, template: saved }}
      enableReinitialize
      onSubmit={values => save.mutateAsync(values).then(() => {}).catch(() => {})}
    >
      {formik => (
        <Form aria-label={accessibleLabel} className="min-w-0 space-y-2">
          <div className={styles.field}>
            <FormTextarea
              id={`ai-wording-${channel}-${language}`}
              name="template"
              label={fieldLabel}
              aria-label={accessibleLabel}
              disabled={disabled || formik.isSubmitting}
              maxLength={1000}
              rows={2}
              dir="auto"
              className="min-h-[60px]"
              placeholder={renderAiDisclosureTemplate(defaultTemplate, storeName)}
            />
          </div>
          <p className="break-words text-xs leading-relaxed text-muted-foreground">
            Preview: {renderAiDisclosureTemplate(formik.values.template || defaultTemplate, storeName)}
          </p>
          {formik.dirty && (
            <Button type="submit" size="sm" variant="outline" className="h-7 px-2 text-xs" aria-label={`Save ${accessibleLabel}`} disabled={disabled || formik.isSubmitting}>
              {formik.isSubmitting && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Save
            </Button>
          )}
        </Form>
      )}
    </Formik>
  )
}
